import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [showChatbot, setShowChatbot] = useState(false);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDashboardData({
        total_requests: 1247,
        total_errors: 23,
        maintenance_mode: false,
        maintenance_message: '',
        last_reset: new Date().toISOString(),
        agent_stats: {
          general_response: { requests: 456, errors: 8 },
          flight_booking: { requests: 234, errors: 5 },
          image_analysis: { requests: 189, errors: 3 },
          language_detection: { requests: 368, errors: 7 }
        },
        disabled_agents: []
      });
      setMaintenanceMessage(dashboardData?.maintenance_message || '');
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dashboardData?.maintenance_message]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Format agent name for display
  const formatAgentName = (name) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Calculate error rate
  const calculateErrorRate = (requests, errors) => {
    if (requests === 0) return '0%';
    return ((errors / requests) * 100).toFixed(1) + '%';
  };

  // Toggle maintenance mode
  const toggleMaintenanceMode = async () => {
    try {
      setDashboardData(prev => ({
        ...prev,
        maintenance_mode: !prev.maintenance_mode,
        maintenance_message: maintenanceMessage
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  // Toggle agent status
  const toggleAgent = async (agentName, enabled) => {
    try {
      setDashboardData(prev => ({
        ...prev,
        disabled_agents: enabled 
          ? prev.disabled_agents.filter(name => name !== agentName)
          : [...prev.disabled_agents, agentName]
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  // Reset stats
  const resetStats = async () => {
    try {
      setDashboardData(prev => ({
        ...prev,
        total_requests: 0,
        total_errors: 0,
        last_reset: new Date().toISOString(),
        agent_stats: Object.keys(prev.agent_stats).reduce((acc, key) => {
          acc[key] = { requests: 0, errors: 0 };
          return acc;
        }, {})
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#f5f7fa', 
      minHeight: '100vh',
      padding: '30px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#004a97',
          padding: '20px 30px',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>J9 AI Assistant Agent Dashboard</h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8 }}>Monitor and control AI agents</p>
          </div>
          <div>
            <button 
              onClick={fetchDashboardData}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 15px',
                color: 'white',
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '14px'
              }}
            >
              Refresh
            </button>
            <button 
              onClick={() => navigate('/admin/controlpanel')}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 15px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Back to Admin
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '30px' }}>
          {loading && !dashboardData ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #004a97',
                borderRadius: '50%',
                margin: '0 auto',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '20px', color: '#666' }}>Loading dashboard data...</p>
            </div>
          ) : error ? (
            <div style={{ 
              backgroundColor: '#fff5f5', 
              color: '#e53e3e', 
              padding: '20px', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold' }}>Error: {error}</p>
              <button 
                onClick={fetchDashboardData}
                style={{
                  backgroundColor: '#e53e3e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 15px',
                  marginTop: '15px',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          ) : dashboardData && (
            <div>
              {/* System Status */}
              <div style={{ 
                backgroundColor: dashboardData.maintenance_mode ? '#fff5f5' : '#f0fff4',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '30px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{ 
                    margin: '0 0 10px', 
                    color: dashboardData.maintenance_mode ? '#e53e3e' : '#38a169',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    System Status: {dashboardData.maintenance_mode ? 'Maintenance Mode' : 'Online'}
                  </h2>
                  <p style={{ margin: 0, color: '#666' }}>
                    {dashboardData.maintenance_mode 
                      ? `Maintenance Message: ${dashboardData.maintenance_message}` 
                      : 'All systems operational'}
                  </p>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <input
                      type="text"
                      value={maintenanceMessage}
                      onChange={(e) => setMaintenanceMessage(e.target.value)}
                      placeholder="Maintenance message"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        marginRight: '10px',
                        width: '300px'
                      }}
                    />
                  </div>
                  <button 
                    onClick={toggleMaintenanceMode}
                    style={{
                      backgroundColor: dashboardData.maintenance_mode ? '#38a169' : '#e53e3e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 15px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {dashboardData.maintenance_mode ? 'Bring System Online' : 'Enable Maintenance Mode'}
                  </button>
                </div>
              </div>

              {/* Stats Overview */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{ 
                  backgroundColor: '#ebf8ff', 
                  padding: '20px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}>
                  <h3 style={{ margin: '0 0 10px', color: '#3182ce', fontSize: '16px' }}>Total Requests</h3>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>{dashboardData.total_requests}</p>
                </div>
                <div style={{ 
                  backgroundColor: '#fff5f5', 
                  padding: '20px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}>
                  <h3 style={{ margin: '0 0 10px', color: '#e53e3e', fontSize: '16px' }}>Total Errors</h3>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>{dashboardData.total_errors}</p>
                </div>
                <div style={{ 
                  backgroundColor: '#f0fff4', 
                  padding: '20px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}>
                  <h3 style={{ margin: '0 0 10px', color: '#38a169', fontSize: '16px' }}>Success Rate</h3>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
                    {dashboardData.total_requests === 0 
                      ? '100%' 
                      : ((1 - dashboardData.total_errors / dashboardData.total_requests) * 100).toFixed(1) + '%'}
                  </p>
                </div>
                <div style={{ 
                  backgroundColor: '#fffaf0', 
                  padding: '20px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}>
                  <h3 style={{ margin: '0 0 10px', color: '#dd6b20', fontSize: '16px' }}>Last Updated</h3>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    {new Date(dashboardData.last_reset).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Agent Performance */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Agent Performance</h2>
                  <button 
                    onClick={resetStats}
                    style={{
                      backgroundColor: '#718096',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 15px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Reset All Stats
                  </button>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '20px'
                }}>
                  {Object.entries(dashboardData.agent_stats).map(([agentName, stats]) => {
                    const isDisabled = dashboardData.disabled_agents.includes(agentName);
                    return (
                      <div 
                        key={agentName}
                        style={{ 
                          backgroundColor: isDisabled ? '#f7fafc' : 'white',
                          border: `1px solid ${isDisabled ? '#e2e8f0' : '#edf2f7'}`,
                          borderRadius: '8px',
                          padding: '20px',
                          boxShadow: isDisabled ? 'none' : '0 2px 5px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '15px'
                        }}>
                          <h3 style={{ 
                            margin: 0, 
                            fontSize: '18px', 
                            fontWeight: 'bold',
                            color: isDisabled ? '#a0aec0' : '#2d3748'
                          }}>
                            {formatAgentName(agentName)}
                          </h3>
                          <div style={{ 
                            backgroundColor: isDisabled ? '#fed7d7' : '#c6f6d5',
                            color: isDisabled ? '#e53e3e' : '#38a169',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {isDisabled ? 'Disabled' : 'Active'}
                          </div>
                        </div>

                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: '10px',
                          marginBottom: '15px'
                        }}>
                          <div>
                            <p style={{ margin: '0 0 5px', fontSize: '12px', color: '#718096' }}>Requests</p>
                            <p style={{ margin: 0, fontWeight: 'bold', color: isDisabled ? '#a0aec0' : '#2d3748' }}>
                              {stats.requests}
                            </p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 5px', fontSize: '12px', color: '#718096' }}>Errors</p>
                            <p style={{ margin: 0, fontWeight: 'bold', color: isDisabled ? '#a0aec0' : '#e53e3e' }}>
                              {stats.errors}
                            </p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 5px', fontSize: '12px', color: '#718096' }}>Error Rate</p>
                            <p style={{ margin: 0, fontWeight: 'bold', color: isDisabled ? '#a0aec0' : '#dd6b20' }}>
                              {calculateErrorRate(stats.requests, stats.errors)}
                            </p>
                          </div>
                        </div>

                        <button 
                          onClick={() => toggleAgent(agentName, !isDisabled)}
                          style={{
                            width: '100%',
                            backgroundColor: isDisabled ? '#4299e1' : '#f56565',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 0',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}
                        >
                          {isDisabled ? 'Enable Agent' : 'Disable Agent'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat with us button */}
      <div style={{
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        zIndex: 1000
      }}>
        <button 
          onClick={() => setShowChatbot(!showChatbot)}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#004a97',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 74, 151, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.backgroundColor = '#0066cc';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = '#004a97';
          }}
          title="Chat with J9 AI"
        >
          💬
        </button>
      </div>

      {/* Chatbot Modal */}
      {showChatbot && (
        <div style={{
          position: 'fixed',
          bottom: '100px',
          right: '30px',
          width: '400px',
          height: '500px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Chatbot Header */}
          <div style={{
            backgroundColor: '#004a97',
            color: 'white',
            padding: '15px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px' }}>J9 AI Assistant</h3>
              <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>How can I help you?</p>
            </div>
            <button
              onClick={() => setShowChatbot(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              ×
            </button>
          </div>

          {/* Chatbot Content */}
          <div style={{
            flex: 1,
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>🤖</div>
            <h4 style={{ margin: '0 0 10px', color: '#2d3748' }}>J9 AI Assistant</h4>
            <p style={{ margin: 0, color: '#718096', fontSize: '14px', lineHeight: '1.5' }}>
              Welcome to the J9 AI Assistant! I can help you with flight bookings, 
              travel information, and answer questions about Jazeera Airways services.
            </p>
            <button
              onClick={() => window.open('/', '_blank')}
              style={{
                marginTop: '20px',
                backgroundColor: '#004a97',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Launch Full Chatbot
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AgentDashboard;